/*
  Goloso Sano : sticky Add to cart visibility controller.

  Essent renders its sticky Add to cart bar as soon as a product page loads.
  The approved behaviour is that it may only appear once the visitor has
  scrolled PAST the normal product Add to cart button, and must hide again as
  soon as that button returns to the viewport.

  The distinction that matters: a main button sitting BELOW the viewport
  (not reached yet) must keep the bar hidden, while a main button that has
  left the viewport UPWARDS reveals it.

  A page can carry more than one Add to cart button. The lander has one in
  the hero and one in the offer block near the foot. The bar then runs
  between them: visible once the first has scrolled away, hidden again as
  soon as the last one comes into view, because a sticky buy button next to
  a real one is two prompts competing for the same click.

  Pages with a single button keep the original behaviour.

  Uses IntersectionObserver, never a continuous scroll listener. Only stable
  selectors are used: the theme's own submit button and Essent's documented
  data-essential-cart-element hook. No generated Essent class names.
*/
(function () {
  "use strict";

  var STICKY = '[data-essential-cart-element="essential-sticky-cart-container"]';
  var MAIN_BTN = 'form[action*="/cart/add"] button[name="add"]';
  var HIDDEN_ATTR = "data-gs-sticky-hidden";
  /* Raise this to make the bar retreat before the last button is actually
     on screen, measured in pixels below the viewport bottom. */
  var HIDE_BEFORE_LAST_PX = 0;

  if (!("IntersectionObserver" in window)) return;

  /* Styles live with the controller rather than in the theme stylesheet, so the
     theme CSS carries no Essent overrides. */
  var style = document.createElement("style");
  style.id = "gs-sticky-atc-visibility";
  style.textContent =
    STICKY + '{transition:opacity .18s ease,transform .18s ease;}' +
    STICKY + '[' + HIDDEN_ATTR + '="true"]{opacity:0;transform:translateY(100%);pointer-events:none;visibility:hidden;}' +
    '@media (prefers-reduced-motion: reduce){' + STICKY + '{transition:none;}}';
  /* Appearance of the bar itself lives in assets/gs-essent.css. Only the
     visibility rules that this controller drives are defined here. */
  document.head.appendChild(style);

  var observer = null;
  var currentBtn = null;
  var currentSignature = "";

  function sticky() { return document.querySelector(STICKY); }

  function setHidden(hidden) {
    var el = sticky();
    if (el) el.setAttribute(HIDDEN_ATTR, hidden ? "true" : "false");
  }

  /* Hidden until proven otherwise, so the bar never flashes on load. */
  function hideInitially() { setHidden(true); }

  function buttons() {
    return document.querySelectorAll(MAIN_BTN);
  }

  function measureNow() {
    var btns = buttons();
    if (!btns.length) { setHidden(true); return; }

    /* Visible only once the first button has fully left the viewport upwards. */
    var pastFirst = btns[0].getBoundingClientRect().bottom <= 0;
    if (btns.length < 2) { setHidden(!pastFirst); return; }

    /* With a second button further down, the bar stands down as soon as that
       one enters the viewport and stays down for the rest of the page. */
    var lastTop = btns[btns.length - 1].getBoundingClientRect().top;
    var reachedLast = lastTop <= window.innerHeight + HIDE_BEFORE_LAST_PX;
    setHidden(!pastFirst || reachedLast);
  }

  function attach() {
    var btns = buttons();
    if (!btns.length) { setHidden(true); return; }

    /* The cart app replaces its bar node on re-render, which resets the
       attribute while the observer is still correct. Re-measure instead of
       leaving the fresh node in its hidden default state. */
    var signature = btns.length + ":" + (btns[0] === currentBtn ? "same" : "new");
    if (observer && signature === currentSignature) { measureNow(); return; }

    if (observer) observer.disconnect();
    currentBtn = btns[0];
    currentSignature = signature;

    /* Every crossing re-measures the whole set rather than reacting to one
       entry, so the first and the last button cannot contradict each other. */
    observer = new IntersectionObserver(measureNow, { threshold: 0 });
    for (var i = 0; i < btns.length; i++) observer.observe(btns[i]);
    measureNow();
  }

  /* Recalculate after resize and orientation change, rAF throttled. */
  var ticking = false;
  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; attach(); measureNow(); });
  }
  /* The observer is the primary mechanism. This rAF throttled scroll listener
     only re-measures, so a stale state after the cart app re-renders its bar
     cannot leave the sticky bar stuck. At most one measurement per frame. */
  var scrollTicking = false;
  function scheduleMeasure() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () { scrollTicking = false; measureNow(); });
  }
  window.addEventListener("scroll", scheduleMeasure, { passive: true });

  window.addEventListener("resize", schedule, { passive: true });
  window.addEventListener("orientationchange", schedule, { passive: true });

  /* Back navigation restores from bfcache without re-running init. */
  window.addEventListener("pageshow", function () { schedule(); });

  /* Variant switches and dynamic product-form updates replace the button node,
     and Essent injects its bar asynchronously, so watch for both. */
  var mo = new MutationObserver(function () {
    var el = sticky();
    if (el && !el.hasAttribute(HIDDEN_ATTR)) hideInitially();
    attach();
  });

  function init() {
    hideInitially();
    attach();
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
